import { useBreakpointValue } from "@gluestack-style/react";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import { IActionsheetRefProps } from "@repo/core/components/layout/Actionsheet/types";
import { useRef, useState, useMemo, useCallback } from "react";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useQuery } from "@tanstack/react-query";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { apiInstance } from "@repo/core/services/instances/api";
import { mapDraftProposals } from "@repo/core/mappers/proposals";
import { Proposals } from "@repo/core/components/layout/ProposalGroup/types";
import { ProposalOptionsItemProps } from "@repo/core/screens/Proposals/screens/ProposalsSearch/components/ProposalOptionsSheet/ProposalOptionsItem/types";
import { filterByText } from "@repo/core/screens/Proposals/screens/ProposalsSearch/utils";
import { ROUTES } from "@repo/core/routes";
import useToast from "@repo/core/components/core/Toast";
import { PROPOSAL_ACTIONS } from "@repo/core/constants/proposals";
import { usePreProposal } from "@repo/core/screens/PreProposal/hooks/usePreProposal";

const proposalsResource = new ProposalsResource(apiInstance);
const draftProposalOptions: Omit<ProposalOptionsItemProps, "onPress">[] = [
  {
    label: "Continuar proposta",
    icon: "pen-square",
    action: PROPOSAL_ACTIONS.EDIT_PROPOSAL
  }
];

export const useDraftController = () => {
  const alertModalRef = useRef<IModalRefProps>(null);

  const { push } = useNavigation();
  const { success } = useToast();
  const { startQueueProposalEdit } = usePreProposal();

  const actionSheetRef = useRef<IActionsheetRefProps>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [searchText, setSearchText] = useState<string>("");

  const [registerId, setRegisterId] = useState<string | null>(null);
  const [proposalOptions, setProposalOptions] = useState<
    Omit<ProposalOptionsItemProps, "onPress">[] | null
  >(null);

  const isMobile = useBreakpointValue({
    base: true,
    md: false
  });

  const {
    data: proposalsData,
    refetch,
    isFetching,
    isLoading,
    dataUpdatedAt
  } = useQuery({
    queryKey: ["draftProposals"],
    queryFn: () => proposalsResource.consultProposals()
  });

  const proposals: Proposals[] = useMemo(() => {
    if (!proposalsData?.data) {
      return [];
    }

    return mapDraftProposals(proposalsData.data);
  }, [proposalsData]);

  const filteredProposals: Proposals[] = useMemo(() => {
    let result = proposals;

    // Filter by text (proposal number or client name)
    if (searchText) {
      result = filterByText(result, searchText);
    }

    return result;
  }, [proposals, searchText]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const handleOpenOptions = (id: string) => {
    setRegisterId(id);
    setProposalOptions(draftProposalOptions.map((option) => ({ ...option })));
    actionSheetRef.current?.open();
  };

  const handleDeleteDraft = () => {
    // TODO: Add delete draft logic

    success("Rascunho deletado com sucesso");

    actionSheetRef.current?.close();
    alertModalRef.current?.close();
  };

  const handleCancelDeleteDraft = () => {
    actionSheetRef.current?.close();
    alertModalRef.current?.close();
  };

  const handleOnPressOption = async (action: string) => {
    switch (action) {
      case PROPOSAL_ACTIONS.EDIT_PROPOSAL:
        actionSheetRef.current?.close();
        if (registerId) {
          await startQueueProposalEdit(registerId, "preProposal");
        }
        break;
      case PROPOSAL_ACTIONS.SEE_PROPOSAL:
        if (registerId) {
          push(ROUTES.proposalsViewInfo, {
            proposalId: registerId
          });
        }
        actionSheetRef.current?.close();
        break;
      case PROPOSAL_ACTIONS.EFFECTIVE_PROPOSAL:
        actionSheetRef.current?.close();
        if (registerId) {
          push(ROUTES.creditProposalPersonalData, {
            proposalId: registerId
          });
        }
        break;
      case PROPOSAL_ACTIONS.DELETE_DRAFT:
        alertModalRef.current?.open();
        break;
      default:
        console.log("action not found");
        break;
    }
  };

  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt) : null;

  return {
    proposals: filteredProposals,
    actionSheetRef,
    handleOnPressOption,
    handleOpenOptions,
    alertModalRef,
    handleDeleteDraft,
    handleCancelDeleteDraft,
    proposalOptions,
    isMobile,
    refreshing: refreshing || isFetching,
    isLoading,
    lastUpdated,
    onRefresh,
    registerId,
    searchText,
    setSearchText
  };
};
